import { supabase } from '../lib/supabase'
import type { Database } from '../lib/supabase'

type Vehicle = Database['public']['Tables']['driving_log_vehicles']['Row']
type VehicleInsert = Database['public']['Tables']['driving_log_vehicles']['Insert']
type VehicleUpdate = Database['public']['Tables']['driving_log_vehicles']['Update']
type Entry = Database['public']['Tables']['driving_log_entries']['Row']
type EntryInsert = Database['public']['Tables']['driving_log_entries']['Insert']
type EntryUpdate = Database['public']['Tables']['driving_log_entries']['Update']

// ============================================
// Vehicles
// ============================================

export const drivingLogVehiclesService = {
  async getAll(includeInactive = false): Promise<Vehicle[]> {
    let query = supabase
      .from('driving_log_vehicles')
      .select('*')
      .order('name')

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching vehicles:', error)
      throw error
    }

    return data || []
  },

  async create(vehicle: VehicleInsert): Promise<Vehicle> {
    const { data, error } = await supabase
      .from('driving_log_vehicles')
      .insert([vehicle])
      .select()
      .single()

    if (error) {
      console.error('Error creating vehicle:', error)
      throw error
    }

    return data
  },

  async update(id: string, updates: VehicleUpdate): Promise<Vehicle> {
    const { data, error } = await supabase
      .from('driving_log_vehicles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating vehicle:', error)
      throw error
    }

    return data
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('driving_log_vehicles')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting vehicle:', error)
      throw error
    }
  }
}

// ============================================
// Entries
// ============================================

export const drivingLogEntriesService = {
  async getOpenTrip(vehicleId: string): Promise<Entry | null> {
    const { data, error } = await supabase
      .from('driving_log_entries')
      .select('*')
      .eq('vehicle_id', vehicleId)
      .is('odometer_end', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Error fetching open trip:', error)
      throw error
    }

    return data
  },

  async getByVehicle(vehicleId: string, dateFrom: string, dateTo: string): Promise<Entry[]> {
    const { data, error } = await supabase
      .from('driving_log_entries')
      .select('*')
      .eq('vehicle_id', vehicleId)
      .gte('date', dateFrom)
      .lte('date', dateTo)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching entries:', error)
      throw error
    }

    return data || []
  },

  async create(entry: EntryInsert): Promise<Entry> {
    const { data, error } = await supabase
      .from('driving_log_entries')
      .insert([entry])
      .select()
      .single()

    if (error) {
      console.error('Error creating entry:', error)
      throw error
    }

    return data
  },

  async update(id: string, updates: EntryUpdate): Promise<Entry> {
    const { data, error } = await supabase
      .from('driving_log_entries')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating entry:', error)
      throw error
    }

    return data
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('driving_log_entries')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting entry:', error)
      throw error
    }
  },

  async getLastEntry(vehicleId: string): Promise<Entry | null> {
    const { data, error } = await supabase
      .from('driving_log_entries')
      .select('*')
      .eq('vehicle_id', vehicleId)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Error fetching last entry:', error)
      throw error
    }

    return data
  },

  async getAssignmentsForDate(date: string): Promise<{
    id: string
    title: string
    start_date: string
    customer_name: string
    address: string
  }[]> {
    // Hämta uppdrag som:
    // 1. Har start_date = datum (planerade för denna dag), ELLER
    // 2. Är pågående (status = 'pagaende') oavsett startdatum
    const { data, error } = await supabase
      .from('assignments')
      .select(`
        id,
        title,
        start_date,
        status,
        customers!inner(full_name, address_street, address_city)
      `)
      .or(`start_date.eq.${date},status.eq.pagaende`)

    if (error) {
      console.error('Error fetching assignments for date:', error)
      throw error
    }

    return (data || []).map((a: Record<string, unknown>) => {
      const customer = a.customers as { full_name: string; address_street: string; address_city: string } | null
      return {
        id: a.id as string,
        title: a.title as string,
        start_date: a.start_date as string,
        customer_name: customer?.full_name || '',
        address: customer ? `${customer.address_street}, ${customer.address_city}` : ''
      }
    })
  }
}
